<template>
  <div class="lp">

    <!-- ======= NAV ======= -->
    <nav class="lp-nav" :class="{ 'lp-nav--hidden': navHidden, 'lp-nav--scrolled': navScrolled }">
      <div class="lp-nav__inner lp-inner">
        <a class="lp-nav__logo" href="#top" @click.prevent="scrollToId('top')">
          <span class="logo-mark">27pub</span>
          <span class="logo-sub">Poker Management</span>
        </a>
        <div class="lp-nav__links" :class="{ 'lp-nav__links--open': mobileMenuOpen }">
          <a @click.prevent="scrollToId('bankroll'); mobileMenuOpen = false">Bankroll</a>
          <a @click.prevent="scrollToId('tournaments'); mobileMenuOpen = false">Tournaments</a>
          <a @click.prevent="scrollToId('about'); mobileMenuOpen = false">About</a>
          <a @click.prevent="scrollToId('beta'); mobileMenuOpen = false">Beta</a>
          <button class="lp-btn lp-btn--ghost mobile-nav-cta" @click="signinDialog = true; mobileMenuOpen = false">Sign In</button>
          <button class="lp-btn lp-btn--accent mobile-nav-cta" @click="goSignup">Join the Beta</button>
        </div>
        <div class="lp-nav__actions">
          <button class="lp-btn lp-btn--ghost" @click="signinDialog = true">Sign In</button>
          <button class="lp-btn lp-btn--accent lp-nav__cta" @click="goSignup">Join the Beta</button>
        </div>
        <button class="lp-hamburger" :class="{ open: mobileMenuOpen }" @click="mobileMenuOpen = !mobileMenuOpen" aria-label="Menu">
          <span></span><span></span><span></span>
        </button>
      </div>
    </nav>

    <div id="top"></div>

    <!-- ======= HERO ======= -->
    <section class="lp-hero">
      <div class="lp-hero__inner lp-inner">
        <div class="lp-hero__copy lp-hero-animate">
          <span class="lp-eyebrow" style="animation-delay: 0ms">POKER MANAGEMENT</span>
          <h1 class="lp-hero__h1" style="animation-delay: 80ms">Finally know if you're actually winning.</h1>
          <p class="lp-hero__sub" style="animation-delay: 160ms">Track your bankroll, manage your tournaments, and stop pretending about past games.</p>
          <div class="lp-hero__actions" style="animation-delay: 240ms">
            <button class="lp-btn lp-btn--accent lp-btn--lg" @click="goSignup">Join the Beta — It's Free</button>
            <a class="lp-btn lp-btn--text" @click.prevent="scrollToId('how-it-works')">See how it works ↓</a>
          </div>
          <p class="lp-trust" style="animation-delay: 240ms">No credit card. No pressure. Just your numbers.</p>
        </div>
        <div class="lp-hero__visual lp-hero-animate" style="animation-delay: 120ms">
          <div class="lp-mockup">
            <div class="lp-mockup__bar">
              <span class="lp-mockup__dot lp-mockup__dot--r"></span>
              <span class="lp-mockup__dot lp-mockup__dot--y"></span>
              <span class="lp-mockup__dot lp-mockup__dot--g"></span>
              <span class="lp-mockup__title">27pub Bankroll</span>
            </div>
            <div class="lp-mockup__body">
              <div class="lp-mockup__hero-stat">
                <span class="lp-mockup__val up">+$1,240</span>
                <span class="lp-mockup__lbl">This month</span>
              </div>
              <div class="lp-mockup__chart">
                <svg viewBox="0 0 240 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="hGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#2D6A4F" stop-opacity="0.35"/>
                      <stop offset="100%" stop-color="#2D6A4F" stop-opacity="0"/>
                    </linearGradient>
                  </defs>
                  <polygon points="0,76 28,64 58,68 90,48 118,52 148,32 178,22 210,14 240,8 240,80 0,80" fill="url(#hGrad)"/>
                  <polyline points="0,76 28,64 58,68 90,48 118,52 148,32 178,22 210,14 240,8" stroke="#2D6A4F" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="lp-mockup__mini-row">
                <div class="lp-mockup__mini"><span class="val">14</span><span class="lbl">Sessions</span></div>
                <div class="lp-mockup__mini"><span class="val">$18/hr</span><span class="lbl">Hourly</span></div>
                <div class="lp-mockup__mini"><span class="val up">71%</span><span class="lbl">Win rate</span></div>
              </div>
              <div class="lp-mockup__sessions">
                <div class="lp-mockup__srow"><span>Tue Casino Night</span><span class="up">+$340</span></div>
                <div class="lp-mockup__srow"><span>Home Game MTT</span><span class="down">-$80</span></div>
                <div class="lp-mockup__srow"><span>Friday Cash Game</span><span class="up">+$980</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ======= TOURNAMENTS ======= -->
    <section class="lp-feature lp-feature--tint" id="tournaments" ref="tourEl">
      <div class="lp-feature__inner lp-inner" :class="{ 'lp-reveal': tourVisible }">
        <div class="lp-feature__copy">
          <span class="lp-sub-tag">27PUB TOURNAMENTS</span>
          <h2 class="lp-feature__h2">Run your tournament. We'll handle the rest.</h2>
          <p class="lp-feature__body">From setup to final table, 27pub Tournaments handles the details so you can focus on the game. Manage buy-ins, track your player roster, and let everyone follow along in real time.</p>
          <ul class="lp-flist">
            <li><span class="fli">⚙️</span><span><strong>Tournament Setup</strong> — Structure, blinds, and payouts in seconds</span></li>
            <li><span class="fli">🔄</span><span><strong>Buy-in &amp; Rebuy Tracking</strong> — Full cost tracking per player, per tournament</span></li>
            <li><span class="fli">👥</span><span><strong>Player Rosters</strong> — Named players, linked accounts, live chip counts</span></li>
            <li><span class="fli">💰</span><span><strong>Bankroll Integration</strong> — Results sync directly to each player's bankroll</span></li>
            <li><span class="fli">📱</span><span><strong>Multi-device Live View</strong> — Players follow along on their own screen</span></li>
          </ul>
          <a class="lp-feature-cta" @click.prevent="scrollToId('beta')">Explore Tournaments →</a>
        </div>
        <div class="lp-feature__visual">
          <div class="lp-card-mock">
            <div class="lcm-header">
              <span class="lcm-tag">🏆 Tournaments</span>
              <span class="lcm-meta">12 events</span>
            </div>
            <div class="lcm-rows">
              <div class="lcm-row"><span>WSOP Circuit – Day 2</span><span class="up">+$1,240</span></div>
              <div class="lcm-row"><span>Home Game MTT #14</span><span class="down">-$60</span></div>
              <div class="lcm-row"><span>Charity Poker Night</span><span class="up">+$220</span></div>
              <div class="lcm-row"><span>Commerce Casino Daily</span><span class="down">-$125</span></div>
            </div>
            <div class="lcm-footer">
              <span class="lcm-stat">ITM Rate <strong>34%</strong></span>
              <span class="lcm-stat">ROI <strong class="up">+18%</strong></span>
              <span class="lcm-stat">Sessions <strong>12</strong></span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ======= BANKROLL ======= -->
    <section class="lp-feature lp-feature--white" id="bankroll" ref="bankrollEl">
      <div class="lp-feature__inner lp-feature__inner--rev lp-inner" :class="{ 'lp-reveal': bankrollVisible }">
        <div class="lp-feature__visual">
          <div class="lp-card-mock lp-card-mock--dark">
            <div class="lcm-header">
              <span class="lcm-tag">💰 Bankroll</span>
              <span class="lcm-meta">All time</span>
            </div>
            <div class="lcm-stats-row">
              <div class="lcm-mini"><div class="val up">$3,840</div><div class="lbl">Total</div></div>
              <div class="lcm-mini"><div class="val">$22/hr</div><div class="lbl">Hourly</div></div>
              <div class="lcm-mini"><div class="val up">68%</div><div class="lbl">Win rate</div></div>
            </div>
            <div class="lcm-chart">
              <svg viewBox="0 0 240 65" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="bkGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#22C55E" stop-opacity="0.22"/>
                    <stop offset="100%" stop-color="#22C55E" stop-opacity="0"/>
                  </linearGradient>
                </defs>
                <polygon points="0,60 25,52 55,54 82,38 112,42 140,24 170,26 200,12 230,8 240,6 240,65 0,65" fill="url(#bkGrad)"/>
                <polyline points="0,60 25,52 55,54 82,38 112,42 140,24 170,26 200,12 230,8 240,6" stroke="#22C55E" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
        <div class="lp-feature__copy">
          <span class="lp-sub-tag">27PUB BANKROLL</span>
          <h2 class="lp-feature__h2">Stop guessing. Start knowing.</h2>
          <p class="lp-feature__body">You play every week. You think you're up. Probably. Maybe. 27pub Bankroll tracks every session so you actually know — win rate, hourly rate, and whether Tuesday night cash games are worth your time.</p>
          <ul class="lp-flist">
            <li><span class="fli">💰</span><span><strong>Session Logging</strong> — Log a session in under 30 seconds</span></li>
            <li><span class="fli">📈</span><span><strong>Bankroll Graph</strong> — See your all-time trajectory at a glance</span></li>
            <li><span class="fli">⏱</span><span><strong>Hourly Rate</strong> — Know exactly what you make per hour</span></li>
            <li><span class="fli">🎯</span><span><strong>Stake Goals</strong> — Track progress toward moving up in stakes</span></li>
          </ul>
          <a class="lp-feature-cta" @click.prevent="scrollToId('beta')">Explore Bankroll tracking →</a>
        </div>
      </div>
    </section>

    <!-- ======= HOW IT WORKS ======= -->
    <section class="lp-how" id="how-it-works" ref="howEl">
      <div class="lp-how__inner lp-inner" :class="{ 'lp-reveal': howVisible }">
        <div class="lp-section-hdr">
          <span class="lp-eyebrow">HOW IT WORKS</span>
          <h2>Up and running before the next hand is dealt.</h2>
        </div>
        <div class="lp-how__steps">
          <div class="lp-how__step" v-for="(step, i) in steps" :key="i">
            <div class="lp-how__icon">{{ step.icon }}</div>
            <div class="lp-how__num">0{{ i + 1 }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ======= BETA ======= -->
    <section class="lp-beta" id="beta" ref="betaEl">
      <div class="lp-beta__inner lp-inner" :class="{ 'lp-reveal': betaVisible }">
        <div class="lp-section-hdr">
          <span class="lp-eyebrow lp-eyebrow--green">EARLY ACCESS</span>
          <h2>Be the first to know if you're winning.</h2>
          <p class="lp-section-sub">27pub is in beta and we're looking for players who take their game seriously — even if they play just for fun. Join the waitlist and get first access when we open doors.</p>
        </div>
        <div class="lp-beta-card">
          <ul class="lp-beta-perks">
            <li v-for="perk in betaPerks" :key="perk">{{ perk }}</li>
          </ul>
          <div class="lp-signup-form">
            <input v-model="betaEmail" type="email" placeholder="your@email.com" class="lp-email-input" @keyup.enter="goSignup" />
            <button class="lp-btn lp-btn--accent lp-btn--lg" @click="goSignup">Join the Beta</button>
          </div>
          <p class="lp-trust lp-trust--center">No credit card. No commitment. Just early access.</p>
        </div>
      </div>
    </section>

    <!-- ======= FINAL CTA ======= -->
    <section class="lp-final">
      <div class="lp-final__inner lp-inner">
        <h2 class="lp-final__h2">Ready to actually know if you're winning?</h2>
        <p class="lp-final__sub">Join players who stopped guessing. Free beta access. Takes 30 seconds to request your spot.</p>
        <div class="lp-signup-form lp-signup-form--centered">
          <input v-model="finalEmail" type="email" placeholder="your@email.com" class="lp-email-input lp-email-input--dark" @keyup.enter="goSignup" />
          <button class="lp-btn lp-btn--accent lp-btn--lg" @click="goSignup">Get Beta Access</button>
        </div>
        <p class="lp-final-trust">No credit card. No commitment. We'll email you when your spot is ready.</p>
      </div>
    </section>

    <!-- ======= FOOTER ======= -->
    <footer class="lp-footer" id="about">
      <div class="lp-footer__inner lp-inner">
        <div class="lp-footer__brand">
          <span class="logo-mark">27pub</span>
          <span class="lp-footer__tagline">Poker Management</span>
          <p class="lp-footer__origin">27pub is named after the 2-7 offsuit — the worst hand in poker. We know the game from the bottom up.</p>
        </div>
        <div class="lp-footer__links">
          <div class="lp-footer__col">
            <h4>Product</h4>
            <a @click.prevent="scrollToId('bankroll')">Bankroll</a>
            <a @click.prevent="scrollToId('tournaments')">Tournaments</a>
            <a @click.prevent="scrollToId('beta')">Beta</a>
          </div>
          <div class="lp-footer__col">
            <h4>Company</h4>
            <a href="#">About</a>
            <a href="#">Blog</a>
            <a href="#">Contact</a>
            <router-link to="/privacy-policy">Privacy</router-link>
          </div>
        </div>
      </div>
      <div class="lp-footer__strip lp-inner">
        <span>© 2026 27pub. All rights reserved. Built by players, for players.</span>
        <div class="lp-footer__strip-links">
          <router-link to="/login">Sign In</router-link>
          <router-link to="/signup">Join Beta</router-link>
        </div>
      </div>
    </footer>

  <!-- ======= SIGN IN MODAL ======= -->
  <div v-if="signinDialog" class="lp-modal-backdrop" @click.self="signinDialog = false">
    <div class="lp-modal">
      <button class="lp-modal__close" @click="signinDialog = false" aria-label="Close">✕</button>
      <div class="lp-modal__brand">
        <span class="logo-mark">27pub</span>
        <span class="logo-sub">Poker Management</span>
      </div>
      <div class="lp-modal__header">
        <h2>Sign in</h2>
        <p>Know where you stand. Every hand.</p>
      </div>
      <form class="lp-modal__form" @submit.prevent="handleSignin">
        <div class="lp-field">
          <label>Email</label>
          <input v-model="signinEmail" type="email" required autocomplete="email" placeholder="you@example.com" />
        </div>
        <div class="lp-field">
          <label>Password</label>
          <input v-model="signinPassword" type="password" required autocomplete="current-password" placeholder="••••••••" @keydown.enter.prevent="handleSignin" />
        </div>
        <div v-if="signinError" class="lp-modal__error">{{ signinError }}</div>
        <button class="lp-btn lp-btn--accent lp-btn--lg lp-btn--full" type="submit" :disabled="signinLoading">
          {{ signinLoading ? 'Signing in…' : 'Sign in' }}
        </button>
        <button type="button" class="lp-modal__forgot" @click="openForgotFromModal">Forgot password?</button>
      </form>
      <div class="lp-modal__footer">
        <span>Don't have an account?</span>
        <a @click.prevent="signinDialog = false; goSignup()">Join the Beta</a>
      </div>
    </div>
  </div>

  <!-- ======= FORGOT PASSWORD MODAL ======= -->
  <div v-if="forgotDialog" class="lp-modal-backdrop" @click.self="forgotDialog = false">
    <div class="lp-modal lp-modal--sm">
      <button class="lp-modal__close" @click="forgotDialog = false" aria-label="Close">✕</button>
      <div class="lp-modal__header">
        <h2>Reset password</h2>
        <p>Enter your email and we'll send a reset link.</p>
      </div>
      <form class="lp-modal__form" @submit.prevent="sendResetEmail">
        <div class="lp-field">
          <label>Email</label>
          <input v-model="forgotEmail" type="email" required autocomplete="email" placeholder="you@example.com" />
        </div>
        <div v-if="forgotFeedback" :class="['lp-modal__feedback', forgotFeedbackType === 'success' ? 'lp-modal__feedback--ok' : 'lp-modal__error']">{{ forgotFeedback }}</div>
        <button class="lp-btn lp-btn--accent lp-btn--lg lp-btn--full" type="submit" :disabled="forgotLoading">
          {{ forgotLoading ? 'Sending…' : 'Send reset email' }}
        </button>
      </form>
    </div>
  </div>

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/users';
import { useUiStore } from '@/stores/ui';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';

const router = useRouter();
const userStore = useUserStore();
const uiStore = useUiStore();

// Hide the global nav/footer while the landing page is shown
onMounted(() => { uiStore.navBarVisible = false; });
onUnmounted(() => { uiStore.navBarVisible = true; });

// Redirect already-authenticated users to the app
onMounted(async () => {
  await userStore.checkLogin();
  if (userStore.loggedin) {
    const perms = userStore.user?.permissions;
    if (perms?.admin) router.push({ name: 'AdminView' });
    else if (perms?.poker === 'approved') router.push({ name: 'SessionEntry' });
    else if (perms?.tournament === 'approved') router.push({ name: 'TournamentView' });
    else router.push({ name: 'PendingView' });
  }
});

// ── Sign-in modal ────────────────────────────────────────────────────────────
const signinDialog   = ref(false);
const signinEmail    = ref('');
const signinPassword = ref('');
const signinError    = ref('');
const signinLoading  = ref(false);

const forgotDialog       = ref(false);
const forgotEmail        = ref('');
const forgotFeedback     = ref('');
const forgotFeedbackType = ref('info');
const forgotLoading      = ref(false);

async function handleSignin() {
  signinError.value = '';
  signinLoading.value = true;
  try {
    await userStore.login(signinEmail.value, signinPassword.value);
    signinDialog.value = false;
    const perms = userStore.user?.permissions;
    if (perms?.admin) router.push({ name: 'AdminView' });
    else if (perms?.poker === 'approved') router.push({ name: 'SessionEntry' });
    else if (perms?.tournament === 'approved') router.push({ name: 'TournamentView' });
    else router.push({ name: 'PendingView' });
  } catch (err) {
    signinError.value = err.message || 'An error occurred during sign-in.';
  } finally {
    signinLoading.value = false;
  }
}

function openForgotFromModal() {
  forgotEmail.value = signinEmail.value || '';
  forgotFeedback.value = '';
  forgotFeedbackType.value = 'info';
  signinDialog.value = false;
  forgotDialog.value = true;
}

async function sendResetEmail() {
  forgotFeedback.value = '';
  if (!forgotEmail.value) {
    forgotFeedbackType.value = 'error';
    forgotFeedback.value = 'Please enter an email address.';
    return;
  }
  forgotLoading.value = true;
  try {
    await sendPasswordResetEmail(getAuth(), forgotEmail.value);
    forgotFeedbackType.value = 'success';
    forgotFeedback.value = 'Password reset email sent. Check your inbox.';
  } catch (err) {
    forgotFeedbackType.value = 'error';
    if (err?.code === 'auth/user-not-found') {
      forgotFeedback.value = 'No account found for that email.';
    } else {
      forgotFeedback.value = err?.message || 'Unable to send reset email.';
    }
  } finally {
    forgotLoading.value = false;
  }
}

// ── Nav scroll behavior ──────────────────────────────────────────────────────
const navHidden = ref(false);
const navScrolled = ref(false);
const mobileMenuOpen = ref(false);
let lastScrollY = 0;

function onScroll() {
  const y = window.scrollY;
  navScrolled.value = y > 10;
  navHidden.value = y > lastScrollY && y > 100;
  lastScrollY = y;
}

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}

function goSignup() {
  router.push({ name: 'UserSignup' });
}

// ── Scroll reveal ────────────────────────────────────────────────────────────
const tourEl     = ref(null); const tourVisible     = ref(false);
const bankrollEl = ref(null); const bankrollVisible = ref(false);
const howEl      = ref(null); const howVisible      = ref(false);
const betaEl     = ref(null); const betaVisible     = ref(false);

function observe(el, visibleRef, cb) {
  if (!el) return;
  const obs = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) { visibleRef.value = true; cb?.(); obs.disconnect(); }
  }, { threshold: 0.1 });
  obs.observe(el);
}

// ── Static content ───────────────────────────────────────────────────────────
const steps = [
  { icon: '✋', title: 'Request beta access',  body: "Drop your email. We'll get you in. No credit card, no commitment — just early access." },
  { icon: '🃏', title: 'Run your game',         body: "Play your cash games and tournaments. Log results in seconds. We track cash sessions, home games, and tournaments." },
  { icon: '📊', title: 'See your real numbers', body: "Your dashboard shows exactly where you stand. No more guessing. No more lying to yourself." },
];

const betaPerks = [
  '🏆  Free access during the entire beta period',
  '📣  Direct line to the team — your feedback shapes the product',
  '🔒  Locked-in early adopter pricing when we launch',
  '🎯  Priority access to new features before anyone else',
];

const betaEmail  = ref('');
const finalEmail = ref('');

// ── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
  lastScrollY = window.scrollY;
  observe(tourEl.value,     tourVisible);
  observe(bankrollEl.value, bankrollVisible);
  observe(howEl.value,      howVisible);
  observe(betaEl.value,     betaVisible);
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@800&family=DM+Sans:ital,wght@0,400;0,600;0,700;1,400&family=DM+Mono:wght@500&display=swap');

/* ── Variables ── */
.lp {
  --c-warm:        #F7F0E6;
  --c-green:       #2D6A4F;
  --c-accent:      #E07A2F;
  --c-accent-dark: #C06928;
  --c-dark:        #1A1A2E;
  --c-gray:        #6B7280;
  --c-white:       #FFFFFF;
  --c-green-light: #EAF4EE;
  --c-success:     #22C55E;
  --c-error:       #EF4444;
  --r-card:        12px;
  --r-btn:         8px;
  --nav-h:         64px;

  font-family: 'DM Sans', sans-serif;
  color: var(--c-dark);
  background: var(--c-warm);
  overflow-x: hidden;
}

.lp *, .lp *::before, .lp *::after { box-sizing: border-box; margin: 0; padding: 0; }
.lp a { cursor: pointer; text-decoration: none; color: inherit; }
.lp ul { list-style: none; }
.up   { color: var(--c-success); }
.down { color: var(--c-error); }

/* ── Layout ── */
.lp-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 80px;
}

/* ── Reveal animation ── */
.lp-reveal {
  animation: lpReveal 0.45s ease-out both;
}
@keyframes lpReveal {
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Buttons ── */
.lp-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: var(--r-btn);
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
  white-space: nowrap;
}
.lp-btn--accent {
  background: var(--c-accent);
  color: #fff;
}
.lp-btn--accent:hover {
  background: var(--c-accent-dark);
  box-shadow: 0 4px 14px rgba(224,122,47,0.35);
}
.lp-btn--accent:active { transform: scale(0.98); }
.lp-btn--lg { font-size: 16px; padding: 14px 28px; }
.lp-btn--text {
  background: transparent;
  color: var(--c-green);
  padding: 12px 4px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.lp-btn--text:hover { color: var(--c-accent); }

/* ── Type helpers ── */
.lp-eyebrow {
  display: inline-block;
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.1em;
  color: var(--c-green);
  background: var(--c-green-light);
  padding: 5px 10px;
  border-radius: 4px;
  margin-bottom: 16px;
}
.lp-eyebrow--green { color: var(--c-green); }
.lp-sub-tag {
  display: inline-block;
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  color: var(--c-green);
  background: var(--c-green-light);
  padding: 5px 10px;
  border-radius: 4px;
  margin-bottom: 20px;
}
.lp-trust {
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  color: var(--c-gray);
  margin-top: 14px;
}
.lp-trust--center { text-align: center; }

/* ── Section header ── */
.lp-section-hdr {
  text-align: center;
  margin-bottom: 56px;
}
.lp-section-hdr h2 {
  font-family: 'DM Sans', sans-serif;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 700;
  color: var(--c-dark);
  margin-top: 4px;
  line-height: 1.2;
}
.lp-section-sub {
  font-size: 17px;
  color: var(--c-gray);
  max-width: 600px;
  margin: 16px auto 0;
  line-height: 1.65;
}

/* ══════════════════════════════════════════
   NAV
══════════════════════════════════════════ */
.lp-nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 1000;
  height: var(--nav-h);
  background: var(--c-white);
  transition: transform 0.3s ease, box-shadow 0.2s ease;
}
.lp-nav--scrolled { box-shadow: 0 1px 16px rgba(0,0,0,0.08); }
.lp-nav--hidden   { transform: translateY(-100%); }

.lp-nav__inner {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 40px;
}

.lp-nav__logo {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-shrink: 0;
}
.logo-mark {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 22px;
  color: var(--c-green);
}
.logo-sub {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: var(--c-gray);
}

.lp-nav__links {
  display: flex;
  align-items: center;
  gap: 32px;
  flex: 1;
  justify-content: center;
}
.lp-nav__links a {
  font-size: 15px;
  font-weight: 600;
  color: var(--c-dark);
  transition: color 0.15s;
}
.lp-nav__links a:hover { color: var(--c-green); }
.mobile-nav-cta { display: none; }

.lp-nav__cta { flex-shrink: 0; }

.lp-hamburger {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-left: auto;
}
.lp-hamburger span {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--c-dark);
  transition: all 0.2s ease;
  border-radius: 2px;
}
.lp-hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.lp-hamburger.open span:nth-child(2) { opacity: 0; }
.lp-hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* ══════════════════════════════════════════
   HERO
══════════════════════════════════════════ */
.lp-hero {
  background: var(--c-warm);
  padding: calc(var(--nav-h) + 80px) 0 96px;
  min-height: 100vh;
  display: flex;
  align-items: center;
}
.lp-hero__inner {
  display: grid;
  grid-template-columns: 55fr 45fr;
  gap: 64px;
  align-items: center;
}
.lp-hero__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.lp-hero__h1 {
  font-family: 'Playfair Display', serif;
  font-weight: 800;
  font-size: clamp(40px, 5.5vw, 64px);
  line-height: 1.1;
  color: var(--c-dark);
  margin-bottom: 20px;
}
.lp-hero__sub {
  font-size: 18px;
  color: var(--c-gray);
  line-height: 1.7;
  max-width: 540px;
  margin-bottom: 36px;
}
.lp-hero__actions {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

/* Hero load animation */
.lp-hero-animate > * {
  animation: heroFadeUp 0.55s ease-out both;
}
.lp-hero-animate {
  animation: heroFadeUp 0.55s ease-out both;
}
@keyframes heroFadeUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* App mockup visual */
.lp-mockup {
  background: var(--c-white);
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
  max-width: 420px;
  margin-left: auto;
}
.lp-mockup__bar {
  background: #f0f0f0;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.lp-mockup__dot {
  width: 11px; height: 11px;
  border-radius: 50%;
}
.lp-mockup__dot--r { background: #FF6058; }
.lp-mockup__dot--y { background: #FFBD2E; }
.lp-mockup__dot--g { background: #28C840; }
.lp-mockup__title {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: var(--c-gray);
  margin-left: 8px;
}
.lp-mockup__body { padding: 20px; }
.lp-mockup__hero-stat {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}
.lp-mockup__val {
  font-family: 'Playfair Display', serif;
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
}
.lp-mockup__lbl {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: var(--c-gray);
  margin-top: 4px;
}
.lp-mockup__chart { margin-bottom: 16px; }
.lp-mockup__chart svg { width: 100%; height: 80px; }
.lp-mockup__mini-row {
  display: flex;
  gap: 0;
  margin-bottom: 16px;
  background: var(--c-green-light);
  border-radius: 8px;
  padding: 12px 0;
}
.lp-mockup__mini {
  flex: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-right: 1px solid rgba(45,106,79,0.15);
}
.lp-mockup__mini:last-child { border-right: none; }
.lp-mockup__mini .val {
  font-weight: 700;
  font-size: 15px;
  color: var(--c-dark);
}
.lp-mockup__mini .lbl {
  font-family: 'DM Mono', monospace;
  font-size: 10px;
  color: var(--c-gray);
}
.lp-mockup__sessions { display: flex; flex-direction: column; gap: 8px; }
.lp-mockup__srow {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 6px 8px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
}
.lp-mockup__srow span:first-child { color: var(--c-dark); }
.lp-mockup__srow span:last-child { font-weight: 600; }

/* ══════════════════════════════════════════
   SOCIAL PROOF BAR
══════════════════════════════════════════ */
.lp-proof {
  background: var(--c-green-light);
  padding: 48px 0;
}
.lp-proof__inner {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
}
.lp-proof__stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0 24px;
  border-right: 1px solid rgba(45,106,79,0.2);
}
.lp-proof__stat:last-child { border-right: none; }
.lp-proof__num {
  font-family: 'Playfair Display', serif;
  font-size: 36px;
  font-weight: 800;
  color: var(--c-green);
  line-height: 1;
}
.lp-proof__lbl {
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  color: var(--c-gray);
  text-align: center;
}

/* ══════════════════════════════════════════
   FEATURE SECTIONS
══════════════════════════════════════════ */
.lp-feature { padding: 96px 0; }
.lp-feature--tint  { background: var(--c-green-light); }
.lp-feature--white { background: var(--c-white); }

.lp-feature__inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}
.lp-feature__inner--rev .lp-feature__visual { order: -1; }

.lp-feature__h2 {
  font-family: 'DM Sans', sans-serif;
  font-size: clamp(26px, 3.5vw, 38px);
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 16px;
  color: var(--c-dark);
}
.lp-feature__body {
  font-size: 17px;
  color: var(--c-gray);
  line-height: 1.7;
  margin-bottom: 28px;
}
.lp-flist { display: flex; flex-direction: column; gap: 14px; margin-bottom: 28px; }
.lp-flist li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  color: var(--c-dark);
  line-height: 1.5;
}
.fli { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
.lp-feature-cta {
  font-size: 15px;
  font-weight: 700;
  color: var(--c-accent);
  border-bottom: 2px solid var(--c-accent);
  padding-bottom: 2px;
  transition: color 0.15s;
  cursor: pointer;
}
.lp-feature-cta:hover { color: var(--c-accent-dark); border-color: var(--c-accent-dark); }

/* Card mock (feature illustration) */
.lp-card-mock {
  background: var(--c-white);
  border: 1px solid #E5E7EB;
  border-radius: var(--r-card);
  padding: 24px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.07);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.lp-card-mock:hover {
  box-shadow: 0 12px 32px rgba(0,0,0,0.12);
  transform: translateY(-4px);
}
.lp-card-mock--dark { background: var(--c-dark); }
.lp-card-mock--dark .lcm-header .lcm-tag,
.lp-card-mock--dark .lcm-header .lcm-meta { color: #fff; }
.lp-card-mock--dark .lcm-chart { border-color: rgba(255,255,255,0.08); }
.lp-card-mock--dark .lcm-mini .lbl { color: rgba(255,255,255,0.5); }
.lp-card-mock--dark .lcm-mini .val { color: #fff; }

.lcm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.lcm-tag {
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  color: var(--c-green);
}
.lcm-meta {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: var(--c-gray);
}
.lcm-rows { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
.lcm-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: var(--c-dark);
  padding: 8px 10px;
  background: #f9fafb;
  border-radius: 6px;
}
.lcm-row span:last-child { font-weight: 700; }
.lcm-footer {
  display: flex;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid #E5E7EB;
}
.lcm-stat {
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  color: var(--c-gray);
}
.lcm-stat strong { color: var(--c-dark); }
.lcm-stats-row {
  display: flex;
  gap: 0;
  background: rgba(255,255,255,0.06);
  border-radius: 8px;
  padding: 16px 0;
  margin-bottom: 16px;
}
.lcm-mini {
  flex: 1;
  text-align: center;
  border-right: 1px solid rgba(255,255,255,0.1);
}
.lcm-mini:last-child { border-right: none; }
.lcm-mini .val { font-size: 18px; font-weight: 700; }
.lcm-mini .lbl {
  font-family: 'DM Mono', monospace;
  font-size: 10px;
  margin-top: 3px;
}
.lcm-chart {
  padding-top: 8px;
  border-top: 1px solid rgba(255,255,255,0.08);
}
.lcm-chart svg { width: 100%; height: 65px; }

/* ══════════════════════════════════════════
   HOW IT WORKS
══════════════════════════════════════════ */
.lp-how { background: var(--c-warm); padding: 96px 0; }
.lp-how__steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.lp-how__step {
  background: var(--c-white);
  border: 1px solid #E5E7EB;
  border-radius: var(--r-card);
  padding: 32px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s, transform 0.2s;
}
.lp-how__step:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  transform: translateY(-4px);
}
.lp-how__icon {
  width: 52px; height: 52px;
  background: var(--c-green-light);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 8px;
}
.lp-how__num {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: var(--c-gray);
  margin-bottom: 12px;
}
.lp-how__step h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--c-dark);
  margin-bottom: 10px;
  line-height: 1.3;
}
.lp-how__step p {
  font-size: 15px;
  color: var(--c-gray);
  line-height: 1.6;
}

/* ══════════════════════════════════════════
   TESTIMONIALS
══════════════════════════════════════════ */
.lp-test { background: var(--c-warm); padding: 96px 0; border-top: 1px solid #ece5d8; }
.lp-test__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.lp-test__card {
  background: var(--c-white);
  border: 1px solid #E5E7EB;
  border-radius: var(--r-card);
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s, transform 0.2s;
}
.lp-test__card:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  transform: translateY(-3px);
}
.lp-test__quote {
  font-size: 15px;
  color: var(--c-dark);
  line-height: 1.7;
  font-style: italic;
  flex: 1;
}
.lp-test__attr { display: flex; align-items: center; gap: 12px; }
.lp-test__avatar {
  width: 40px; height: 40px;
  background: var(--c-green);
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'DM Mono', monospace;
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;
}
.lp-test__name {
  font-weight: 700;
  font-size: 14px;
  color: var(--c-dark);
}
.lp-test__role {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: var(--c-gray);
  margin-top: 2px;
}

/* ══════════════════════════════════════════
   BETA
══════════════════════════════════════════ */
.lp-beta { background: var(--c-green-light); padding: 96px 0; }
.lp-beta-card {
  max-width: 600px;
  margin: 0 auto;
  background: var(--c-white);
  border: 2px solid var(--c-green);
  border-radius: var(--r-card);
  padding: 40px;
  box-shadow: 0 4px 24px rgba(45,106,79,0.1);
}
.lp-beta-perks {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 32px;
}
.lp-beta-perks li {
  font-size: 15px;
  color: var(--c-dark);
  line-height: 1.5;
}
.lp-signup-form {
  display: flex;
  gap: 10px;
}
.lp-signup-form--centered { justify-content: center; }
.lp-email-input {
  flex: 1;
  max-width: 320px;
  padding: 14px 16px;
  border: 1.5px solid #D1D5DB;
  border-radius: var(--r-btn);
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--c-dark);
  outline: none;
  transition: border-color 0.15s;
  background: var(--c-white);
}
.lp-email-input:focus { border-color: var(--c-green); }
.lp-email-input--dark {
  background: rgba(255,255,255,0.12);
  border-color: rgba(255,255,255,0.3);
  color: #fff;
}
.lp-email-input--dark::placeholder { color: rgba(255,255,255,0.5); }
.lp-email-input--dark:focus { border-color: rgba(255,255,255,0.7); }

/* ══════════════════════════════════════════
   FINAL CTA
══════════════════════════════════════════ */
.lp-final { background: var(--c-green); padding: 96px 0; text-align: center; }
.lp-final__h2 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(32px, 4.5vw, 48px);
  font-weight: 800;
  color: #fff;
  margin-bottom: 16px;
  line-height: 1.2;
}
.lp-final__sub {
  font-size: 18px;
  color: rgba(255,255,255,0.8);
  max-width: 520px;
  margin: 0 auto 36px;
  line-height: 1.65;
}
.lp-final-trust {
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  color: rgba(255,255,255,0.55);
  margin-top: 14px;
}

/* ══════════════════════════════════════════
   FOOTER
══════════════════════════════════════════ */
.lp-footer { background: var(--c-dark); color: rgba(255,255,255,0.7); padding: 64px 0 0; }
.lp-footer__inner {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 64px;
  padding-bottom: 48px;
}
.lp-footer__brand { max-width: 320px; }
.lp-footer__brand .logo-mark { color: var(--c-green); }
.lp-footer__tagline {
  display: block;
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: rgba(255,255,255,0.4);
  margin-top: 4px;
  margin-bottom: 16px;
}
.lp-footer__origin {
  font-size: 14px;
  line-height: 1.7;
  color: rgba(255,255,255,0.5);
}
.lp-footer__links { display: flex; gap: 64px; }
.lp-footer__col { display: flex; flex-direction: column; gap: 12px; }
.lp-footer__col h4 {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
  font-family: 'DM Mono', monospace;
}
.lp-footer__col a {
  font-size: 14px;
  color: rgba(255,255,255,0.55);
  transition: color 0.15s;
  cursor: pointer;
}
.lp-footer__col a:hover { color: #fff; }
.lp-footer__strip {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 80px;
  border-top: 1px solid rgba(255,255,255,0.08);
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  color: rgba(255,255,255,0.35);
}
.lp-footer__strip-links { display: flex; gap: 20px; }
.lp-footer__strip-links a {
  color: rgba(255,255,255,0.45);
  font-size: 12px;
  transition: color 0.15s;
  cursor: pointer;
}
.lp-footer__strip-links a:hover { color: #fff; }

/* ══════════════════════════════════════════
   RESPONSIVE
══════════════════════════════════════════ */
@media (max-width: 1024px) {
  .lp-inner { padding: 0 40px; }
  .lp-hero__inner { gap: 40px; }
  .lp-feature__inner { gap: 48px; }
  .lp-footer__strip { padding: 20px 40px; }
}

@media (max-width: 768px) {
  .lp-inner { padding: 0 24px; }

  /* Nav mobile */
  .lp-nav__actions { display: none; }
  .lp-hamburger { display: flex; }
  .lp-nav__links {
    display: none;
    position: fixed;
    top: var(--nav-h);
    left: 0; right: 0;
    background: var(--c-white);
    flex-direction: column;
    padding: 24px;
    gap: 20px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    border-top: 1px solid #f0f0f0;
  }
  .lp-nav__links--open { display: flex; }
  .lp-nav__links a { font-size: 17px; }
  .mobile-nav-cta { display: inline-flex; width: 100%; justify-content: center; }

  /* Hero mobile */
  .lp-hero { padding: calc(var(--nav-h) + 40px) 0 64px; min-height: auto; }
  .lp-hero__inner { grid-template-columns: 1fr; gap: 48px; }
  .lp-hero__h1 { font-size: 40px; }
  .lp-hero__sub { font-size: 16px; }
  .lp-mockup { max-width: 100%; margin-left: 0; }

  /* Proof bar */
  .lp-proof__inner { grid-template-columns: repeat(2, 1fr); gap: 24px; }
  .lp-proof__stat { border-right: none; border-bottom: 1px solid rgba(45,106,79,0.15); padding-bottom: 20px; }
  .lp-proof__stat:nth-child(even) { border-right: none; }
  .lp-proof__stat:nth-last-child(-n+2) { border-bottom: none; }

  /* Features */
  .lp-feature { padding: 64px 0; }
  .lp-feature__inner,
  .lp-feature__inner--rev { grid-template-columns: 1fr; gap: 40px; }
  .lp-feature__inner--rev .lp-feature__visual { order: 0; }

  /* How */
  .lp-how { padding: 64px 0; }
  .lp-how__steps { grid-template-columns: 1fr; }

  /* Testimonials */
  .lp-test { padding: 64px 0; }
  .lp-test__grid { grid-template-columns: 1fr; }

  /* Beta */
  .lp-beta { padding: 64px 0; }
  .lp-beta-card { padding: 28px 20px; }
  .lp-signup-form { flex-direction: column; }
  .lp-email-input { max-width: 100%; }
  .lp-btn--lg { width: 100%; justify-content: center; }

  /* Final */
  .lp-final { padding: 64px 0; }
  .lp-signup-form--centered { flex-direction: column; align-items: center; }

  /* Footer */
  .lp-footer__inner { grid-template-columns: 1fr; gap: 40px; }
  .lp-footer__brand { max-width: 100%; }
  .lp-footer__links { gap: 32px; }
  .lp-footer__strip { flex-direction: column; gap: 12px; text-align: center; padding: 20px 24px; }

  /* Section headers */
  .lp-section-hdr { margin-bottom: 36px; }
}

/* ── Ghost button (nav Sign In) ── */
.lp-btn--ghost {
  background: transparent;
  color: var(--c-green);
  border: 1.5px solid var(--c-green);
  padding: 10px 20px;
}
.lp-btn--ghost:hover {
  background: var(--c-green-light);
}
.lp-btn--full { width: 100%; justify-content: center; }

/* ── Nav actions group ── */
.lp-nav__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* ══════════════════════════════════════════
   SIGN-IN MODAL
══════════════════════════════════════════ */
.lp-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(26,26,46,0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
}

.lp-modal {
  background: var(--c-white);
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  padding: 36px 32px 28px;
  position: relative;
  box-shadow: 0 24px 64px rgba(0,0,0,0.2);
}
.lp-modal--sm { max-width: 380px; }

.lp-modal__close {
  position: absolute;
  top: 16px;
  right: 18px;
  background: none;
  border: none;
  font-size: 16px;
  color: var(--c-gray);
  cursor: pointer;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.lp-modal__close:hover { color: var(--c-dark); background: #f0f0f0; }

.lp-modal__brand {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 20px;
}

.lp-modal__header {
  margin-bottom: 24px;
}
.lp-modal__header h2 {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--c-dark);
  margin: 0 0 4px;
}
.lp-modal__header p {
  font-size: 0.875rem;
  color: var(--c-gray);
  margin: 0;
}

.lp-modal__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.lp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.lp-field label {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-dark);
}
.lp-field input {
  padding: 12px 14px;
  border: 1.5px solid #D1D5DB;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--c-dark);
  outline: none;
  transition: border-color 0.15s;
  background: #fff;
}
.lp-field input:focus { border-color: var(--c-green); }

.lp-modal__error {
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #DC2626;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
}
.lp-modal__feedback {
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
}
.lp-modal__feedback--ok {
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  color: #15803D;
}

.lp-modal__forgot {
  background: none;
  border: none;
  font-size: 13px;
  color: var(--c-gray);
  cursor: pointer;
  text-align: center;
  padding: 0;
  transition: color 0.15s;
}
.lp-modal__forgot:hover { color: var(--c-green); }

.lp-modal__footer {
  margin-top: 20px;
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--c-gray);
  border-top: 1px solid #F3F4F6;
  padding-top: 16px;
}
.lp-modal__footer a {
  color: var(--c-green);
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.lp-modal__footer a:hover { text-decoration: underline; }

@media (max-width: 480px) {
  .lp-proof__inner { grid-template-columns: 1fr; }
  .lp-proof__stat { border-bottom: 1px solid rgba(45,106,79,0.15); padding-bottom: 16px; }
  .lp-proof__stat:last-child { border-bottom: none; }
  .lp-hero__actions { flex-direction: column; align-items: flex-start; }
  .lp-footer__links { flex-direction: column; gap: 24px; }
}
</style>
